<template>
  <div
    v-if="currentRecord || startNewProcess || newProcess"
    class="Realisation"
  >
    <div
      v-if="isForbidden"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        401
      </h2>
      <div>{{ $t('Process.401-cpo') }}</div>
    </div>
    <div
      v-else-if="notFound"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        404
      </h2>
      <div>{{ $t('Process.404-notFound') }}</div>
    </div>
    <CategorySelectionModal
      v-else-if="newProcess"
      :selected="category"
      @confirmSelection="handleSetCategory"
    />
    <div v-else>
      <div
        v-if="currentRecord"
        class="d-flex justify-content-between flex-gap-2"
      >
        <RealisationMap
          :connection-enabled="isLocationConnected"
          :class="['process-map', { 'hidden': mapHidden }, { 'active': getShowMap }, { isSticky }]"
          :municipality-code="municipalityCode"
          :map-hidden="mapHidden"
          @isLocationConnected="event => isLocationConnected = event"
          @currentRecord="record => (currentRecord = record, correction = false)"
          @feedback="event => (feedback = event.feedback, feedbackMessage = event.message)"
          @handleInput="handleInput"
          @handleCancelChange="handleCancelChange"
        />
        <div
          v-if="!isAmsterdam"
          class="process-main"
        >
          <RealisationHeader
            :record="record"
            :feedback="feedback"
            :feedback-message="feedbackMessage"
            :step="step"
            :record-step="currentStep"
            :may-edit="mayEdit && isCompletedStep"
            @dismissed="(feedback = null, feedbackMessage = '')"
          >
            <FlowInteraction
              :first="currentStep === 1"
              :last="isFinalStep"
              :step="step"
              :current-step="currentStep"
              :locked="locked"
              :disabled="disabled"
              :completed="completed"
              :is-completed-step="isCompletedStep"
              :disable-next-btn="disableNextBtn"
              :may-edit="mayEdit"
              :open-for-change="openForChange"
              :location-connected="isLocationConnected"
              :record="record"
              @handleNext="handleNext"
              @handlePrevious="handlePrevious"
              @handleSubmitAndNext="handleSubmitAndNext"
              @handleCancelChange="handleCancelChange"
              @handleStartCorrection="handleStartCorrection"
              @handleStartChange="handleStartChange"
              @handleComplete="handleComplete"
              @handleSubmit="saveRecord"
            />
          </RealisationHeader>
          <router-view
            :key="`${routerViewKey}-${$route.path}`"
            ref="form"
            class="process-form"
            :connection-enabled="isLocationConnected"
            @isLocationConnected="event => isLocationConnected = event"
            @currentRecord="record => (currentRecord = record, correction = false)"
            @feedback="event => (feedback = event.feedback, feedbackMessage = event.message)"
            @handleInput="handleInput"
            @handleCancelChange="handleCancelChange"
            @selectedMunicipality="code => municipalityCode = code"
            @isSaving="event => isSaving = event"
          />
        </div>
        <div v-else>
          <router-view
            :key="$route.path"
            class="process-form"
          />
        </div>
        <RealisationSidebar
          v-if="!isAmsterdam"
          :key="sidebarKey"
          :uuid="uuid || ''"
          :disabled="disabled || locked"
          :style="getShowMap && mapHidden ? 'display:none' : ''"
        />
        <Comments
          v-if="!isAmsterdam"
          :record="record"
        />
      </div>
      <StartNewRealisation v-else />
      <div
        v-if="!isAmsterdam && isSaving"
        class="spinner-full-window d-flex"
      >
        <b-spinner
          variant="primary"
          class="spinner"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    class="spinner-full-window d-flex"
  >
    <b-spinner
      variant="primary"
      class="spinner"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CategorySelectionModal from '@/components/realisation/CategorySelectionModal';
import { getProcessByUuid } from '@/services/processes';
import RealisationSidebar from '@/components/realisation/RealisationSidebar';
import RealisationMap from '@/views/RealisationMap';
import RealisationHeader from '@/components/realisation/RealisationHeader.vue';
import FlowInteraction from '@/components/flow/FlowInteraction.vue';
import Comments from '@/components/Comments';
import StartNewRealisation from '@/views/StartNewRealisation.vue';
import BaseRealisationStep from '@/views/BaseRealisationStep.vue';

export default {
  name: 'Realisation',
  components: {
    CategorySelectionModal,
    RealisationSidebar,
    RealisationHeader,
    FlowInteraction,
    Comments,
    RealisationMap,
    StartNewRealisation
  },
  mixins: [BaseRealisationStep],
  data() {
    return {
      isLocationConnected: true,
      loadingStatus: {
        pagedWorkflows: {
          error: 0,
          target: 0,
          current: 0
        }
      },
      failed: false,
      missingWorkflows: [],
      defaultFilter: 'actief',
      realisationsListKey: 0,
      realisationUuid: null,
      category: '',
      isForbidden: false,
      currentRecord: null,
      notFound: false,
      token: '',
      sidebarKey: 0,
      municipalityCode: null,
      isSaving: false,
      mapHidden: false,
      show: false,
      isSticky: false,
      routerViewKey: 0
    };
  },

  computed: {
    ...mapGetters('realisations', ['getSelectedRecord']),
    ...mapGetters('user', ['canAccessRequests', 'canAccessRealisationProcesses', 'roleDetailsMerged']),
    ...mapGetters('tenant', ['isCurrentTenant', 'getSteps']),
    ...mapGetters('processes', ['getSelectedProcess', 'getShowMap', 'getIsSavingProcess', 'getRecordLocked']),
    ...mapGetters('chargingpoints', ['getActiveMunicipality']),
    userIdentity () {
      const identity = this.$auth.user['https://evtools.nl/identity']
      return identity?.uuid //&& identity.category === 'cpo'
        ? this.$auth.user['https://evtools.nl/identity']
        : {}
    },

    newProcess() {
      return this.$route.name === 'NewRealisation' || this.$route.params?.uuid === 'new';
    },
    startNewProcess () {
      return this.$route.name === 'NewRealisationType'
    },
    lastStep () {
      return this.getSteps?.at(-1)?.step
    },
    isAmsterdam () {
      return process.env.VUE_APP_TENANT === 'amsterdam'
    },
    isFinalStep () {
      return this.currentStep === this.lastStep
    },
  },

  watch: {
    $route: {
      deep: true,
      async handler (route) {
        this.feedback = null;
        this.feedbackMessage = ''
        //this.locked = false

        if (this.startNewProcess) {
          this.sidebarKey++
          this.currentRecord = null
          this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: '' })
          return this.$store.dispatch('realisations/setSelectedRecord', { record: null })
        }

        if (!this.currentRecord) {
          const record = await this.fetchRecord()
          this.currentRecord = record
          this.$store.dispatch('realisations/setSelectedRecord', { record })
          return
        }

        if (route.params?.uuid && route.params.uuid !== this.currentRecord?.uuid) {
          this.currentRecord.Docs = {}
          this.$store.dispatch('realisations/setSelectedRecord', { record: this.currentRecord })

          const record = await this.fetchRecord()
          this.currentRecord = record
          this.$store.dispatch('realisations/setSelectedRecord', { record })
        }
        // If change is started (e.g. correction) reset values to previously saved //
        if (this.getSelectedRecord) {
          this.getSelectedRecord.CurrentLocation = JSON.parse(JSON.stringify(this.getSelectedRecord.raw.process))
          this.$store.dispatch('realisations/setSelectedRecord', { record: this.getSelectedRecord })
          this.handleCancelChange()
        }
      }
    },
    getShowMap: {
      handler (value) {
        if (!value) return this.isSticky = false

        setTimeout(() => {
          this.isSticky = value
        }, 300)
      }
    },
    getIsSavingProcess: {
      handler (isSaving) {
        this.isSaving = isSaving
      }
    },
    getRecordLocked (locked) {
      if (locked) {
        // TEMP: After cancelling correction, reset "locked" of current step component //
        this.routerViewKey++
      }
    }
  },

  async created () {
    this.mapHidden = window.innerWidth <= 2100

    this.token = await this.$auth.getTokenSilently();

    if (this.getSelectedRecord?.uuid === this.$route.params.uuid) {
      this.currentRecord = this.getSelectedRecord
    } else {
      this.$store.dispatch('realisations/setSelectedRecord', { record: null })
    }

    // TODO: When refresh takes place on other step then 1, route name is always step1 //
    if (this.$route.params?.step && !this.$route.name.endsWith(this.$route.params.step)) {
      const name = `realisationStep${this.$route.params.step}`
      this.$router.replace({ name, params: this.$route.params })
    }

    this.$store.dispatch('processes/setCurrentProcessType', {
      processType: 'realisation'
    })

    if (this.currentRecord && this.currentRecord?.uuid === this.$route.params?.uuid) {
      //this.currentStep = this.setCurrentStep(this.currentRecord)

      if (!this.$route.params.step && !this.newProcess && !this.startNewProcess) {
        this.$router.replace({ name: `realisationStep${this.currentStep}`, params: { step: this.currentStep } })
      }
    }

    if (this.newProcess || this.startNewProcess || this.getSelectedRecord) {
      return
    }

    const record = await this.fetchRecord()

    this.currentRecord = record
    if (!record) return this.notFound = true;

    this.$store.dispatch('realisations/setSelectedRecord', { record })

    const code = record.MunicipalityCode
    //this.currentStep = this.setCurrentStep(record)
    this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })

    if (record.requestUuids?.length) {
      await this.loadConnectedRequests({ uuids: record.requestUuids, token: this.token })
      // Atm. the old way => add every connected req. to a process //
      if (record.requestUuids?.length) {
        record.requestUuids.forEach(uuid => {
          this.$store.dispatch('relations/addConnection', {
            requestUuid: uuid,
            realisationUuid: record.uuid
          })
        });
      }
    }

    setTimeout(() => {
      this.show = true
    }, 3000)

    if (!this.newProcess && record?.errorMessage) {
      return this.notFound = true
    }

    // if (!record) {
    //   return
    // }

    if (!record && this.userIdentity?.uuid  && !this.$route.name.startsWith('NewRealisation')) {
      return this.isForbidden = true
    }
    // Coming from Request list //
    const fromRequestList = ['aanvragen', '/'].some(type => this.$router.history._startLocation.includes(type))

    if (!this.$route.params.step && fromRequestList) {
      this.$router.replace({ name: `realisationStep${this.currentStep}`, params: { step: this.currentStep } })
    }
  },
  mounted() {
    window.addEventListener('resize', this.setMapState)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setMapState)
  },
  methods: {
    ...mapActions('requests', ['loadConnectedRequests']),

    handleSetCategory(event) {
      this.category = event.category;
      this.$router.replace({ name: 'NewRealisationType', params: { type: event.category } });
    },
    setMapState (e) {
      this.mapHidden = e.target.innerWidth < 2100
    },
    async fetchRecord () {
      if (!this.$route.params?.uuid) return null

      const record = await getProcessByUuid({ token: this.token, uuid: this.$route.params?.uuid })
        .catch(err => console.log(err))

      this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: record.MunicipalityCode })

      if (record?.errorMessage) {
        this.notFound = true
        return false
      }
      return record
    },
    setCurrentStep () {
      return this.currentRecord.processStatus?.currentStep === 100 // Representing 'completed'
        ? this.lastStep
        : this.currentRecord.processStatus?.currentStep
    }
  }
};
</script>
